<template>
  <div
    class="search-modal"
    role="dialog"
  >
    <div
      class="search-scrim"
      @click="$emit('close-modal')"
    />
    <div class="search-box">
      <ais-autocomplete
        role="combobox"
        aria-haspopup="listbox"
      >
        <template #default="{ currentRefinement, indices }">
          <div class="search-wrapper">
            <form
              :id="'search-form-' + searchType"
              role="search"
              class="search-form"
              novalidate
            >
              <label
                :for="'search-input-' + searchType"
                aria-labelledby="search-title"
                class="search-label"
              >
                <i
                  class="icon-lg icon-reg icon-search"
                  aria-hidden="true"
                />
                <span
                  id="search-title"
                  class="sr-only"
                >
                  Search
                </span>
              </label>
              <debounced-search
                v-focus
                :placeholder="placeholder"
                :should-clear-input="shouldClearInput"
                :selected-index="selectedIndex"
                @clear-input="shouldClearInput = false; selectedIndex = 0"
                @focus="selectedIndex = 0; setScrollBoxScrollTop()"
              />
              <button
                v-if="currentRefinement.length > 0"
                type="reset"
                class="search-clear"
                aria-label="Clear"
                @focus="isBtnFocused = true"
                @blur="isBtnFocused = false"
                @click="shouldClearInput = true; selectedIndex = 0"
              >
                <i
                  class="icon-sm icon-reg icon-close"
                  aria-hidden="true"
                />
              </button>
            </form>
            <button
              type="reset"
              class="search-cancel"
              @focus="isBtnFocused = true"
              @blur="isBtnFocused = false"
              @click="shouldClearInput = true; $emit('close-modal')"
            >
              Cancel
            </button>
          </div>
          <section
            v-if="currentRefinement"
            :ref="(el) => scrollBox = el"
            class="results-wrapper"
          >
            <ul
              v-for="index in indices"
              id="search-list"
              :key="index.indexId"
              :ref="(el) => searchResultsRef = {el, total: index.hits.length}"
              role="listbox"
            >
              <template v-if="index.hits.length > 0">
                <search-item
                  v-for="(hit, hitIndex) in index.hits"
                  :key="hit.objectID"
                  :hit="hit"
                  :index="hitIndex"
                  :selected-index="selectedIndex"
                  :is-btn-focused="isBtnFocused"
                  @tab-focused="(el) => selectedIndex = el"
                />
              </template>
              <template v-else>
                <li class="no-results">
                  No results for "<span class="current-refinement">{{ currentRefinement }}</span>"
                </li>
              </template>
            </ul>
          </section>
        </template>
      </ais-autocomplete>
    </div>
  </div>
</template>

<script>
/* Components */
import { AisAutocomplete } from 'vue-instantsearch/vue3/es';
import DebouncedSearch from '../DebouncedSearch.vue';
import SearchItem from './SearchItem.vue';

export default {
  components: {
    AisAutocomplete,
    DebouncedSearch,
    SearchItem
  },

  directives: {
    focus: {
      mounted(el) {
        el.focus();
      }
    },
  },

  inject: {
    searchType: {
      default: 'global',
    },
  },

  emits: {
    'close-modal': null,
  },

  data() {
    return {
      placeholder: 'Search for a topic',
      scrollBox: null,
      selectedIndex: 0,
      searchResultsRef: null,
      shouldClearInput: false,
      isBtnFocused: false,
    };
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyboardEvents);

    /* Add focus trap for screen readers. */
    document.getElementById('app').ariaHidden = true;
  },

  unmounted() {
    window.removeEventListener('keydown', this.handleKeyboardEvents);

    /* Remove focus trap for screen readers. */
    document.getElementById('app').ariaHidden = false;
  },

  methods: {
    /**
     * Iterate `<li>` items backwards and set focus. If selected item is the first item, loop to the last item.
     * 
     * @return void
     */
    handleArrowUp() {
      this.selectedIndex > 0 ? this.selectedIndex-- : this.selectedIndex = this.searchResultsRef.total - 1;
      this.setSearchItemFocus(this.selectedIndex);
      return;
    },

    /**
     * Iterate `<li>` items forwards and set focus. If selected item is the last item, loop to the first item.
     * 
     * @return void
     */
    handleArrowDown() {
      this.selectedIndex < this.searchResultsRef.total - 1 ? this.selectedIndex++ : this.selectedIndex = 0;
      this.setSearchItemFocus(this.selectedIndex);
      return;
    },

    /**
     * Clear the input and close the modal.
     * 
     * @return void
     */
    handleEscape() {
      this.shouldClearInput = true;
      this.$emit('close-modal');
      return;
    },

    /**
     * Implement focus trap that only allows tabbing through modal elements in logical order.
     *
     * @param  e - $event passed from handleKeyboardEvents()
     * @return void
     */
    handleTab(e) {
      const FOCUSABLE_SELECTORS = 'a[href], button, input';
      const focusable = this.$el.querySelectorAll(FOCUSABLE_SELECTORS);
      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];

      /* If tab + shift. */
      if (e.shiftKey) {
        /* If at the beginning of the focusable items, loop back to the end. */
        if (e.target === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        /* If at the end of the focusable items, loop back to the beginning. */
        if (e.target === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }

      return;
    },

    /**
     * Method to switch through and handle all keyboard-related methods.
     *
     * @param  e - $event
     * @return void
     */
    handleKeyboardEvents(e) {
      switch(e.key) {
      case 'ArrowUp':
        this.handleArrowUp();
        break;
      case 'ArrowDown':
        this.handleArrowDown();
        break;
      case 'Escape':
        this.handleEscape();
        break;
      case 'Tab':
        this.handleTab(e);
        break;
      default:
        /* Do nothing. */
      }

      return;
    },

    /**
     * Set the scrollable `<section>` containing the search results back to the top when it losses focus 
     * (when the search input gains focus).
     * 
     * @return void
     */
    setScrollBoxScrollTop() {
      if (this.scrollBox !== null) {
        this.scrollBox.scrollTop = 0;
      }

      return;
    },

    /**
     * Set the search `<li>` item focus based on the `selectedIndex`
     * 
     * @param  int selectedIndex - The selected index passed from component data variable.
     * @return void
     */
    setSearchItemFocus(selectedIndex) {
      const FOCUSABLE_SELECTORS = 'li';
      const focusable = this.$el.querySelectorAll(FOCUSABLE_SELECTORS);

      focusable[selectedIndex].focus();

      return;
    },
  },
};
</script>
